import { createContext, useContext, useCallback, useEffect, useRef, useState, ReactNode } from 'react'

/** After Apply / dismiss, Radix may call onOpenChange(true) when focus returns to the trigger; ignore briefly. */
const FILTER_POPOVER_SUPPRESS_OPEN_MS = 450

interface AppPageStateContextType {
  showFilters: boolean
  /** Radix Popover `onOpenChange` — ignores spurious `true` right after `dismissFilterPopover`. */
  setFilterPopoverOpen: (open: boolean) => void
  /** Close the filters popover and suppress auto-reopen from focus/pointer side-effects. */
  dismissFilterPopover: () => void
  isConfiguringLayout: boolean
  setIsConfiguringLayout: React.Dispatch<React.SetStateAction<boolean>>
  toggleIsConfiguringLayout: () => void
  toggleShowFilters: () => void
}

const AppPageStateContext = createContext<AppPageStateContextType | undefined>(undefined)

interface AppPageProviderProps {
  conFigureLayoutActive?: boolean
  children: ReactNode
}

export function AppPageStateProvider({ children, conFigureLayoutActive }: AppPageProviderProps) {
  const [showFilters, setShowFiltersState] = useState(false)
  const [isConfiguringLayout, setIsConfiguringLayout] = useState(conFigureLayoutActive ?? false)
  const suppressPopoverOpenUntilRef = useRef(0)

  const toggleIsConfiguringLayout = useCallback(
    () => setIsConfiguringLayout(!isConfiguringLayout),
    [isConfiguringLayout]
  )

  const setFilterPopoverOpen = useCallback((open: boolean) => {
    if (open && Date.now() < suppressPopoverOpenUntilRef.current) {
      return
    }
    setShowFiltersState(open)
  }, [])

  const dismissFilterPopover = useCallback(() => {
    suppressPopoverOpenUntilRef.current = Date.now() + FILTER_POPOVER_SUPPRESS_OPEN_MS
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    setShowFiltersState(false)
  }, [])

  const toggleShowFilters = useCallback(() => {
    setShowFiltersState((open) => !open)
  }, [])

  useEffect(() => {
    if (isConfiguringLayout) {
      setShowFiltersState(true)
      return
    }
    setShowFiltersState(false)
  }, [isConfiguringLayout])

  const value = {
    showFilters,
    setFilterPopoverOpen,
    dismissFilterPopover,
    isConfiguringLayout,
    setIsConfiguringLayout,
    toggleIsConfiguringLayout,
    toggleShowFilters,
  }

  return <AppPageStateContext.Provider value={value}>{children}</AppPageStateContext.Provider>
}

export function useAppPageState() {
  const context = useContext(AppPageStateContext)
  if (context === undefined) {
    throw new Error('useAppPageState must be used within an AppPageStateProvider')
  }
  return context
}
